import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { canImportLibrary, libraryError } from '@/lib/libraryAccess';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Download, Eye, History, Library } from 'lucide-react';
import { LibrarySchemaPreview } from '@/components/forms/LibrarySchemaPreview';
import { LibraryImportDialog } from '@/components/forms/LibraryImportDialog';
import { LibraryErrorState, LibraryLoading } from '@/components/forms/LibraryStates';
import { sourceRefLabel, useLibraryBranding } from '@/lib/libraryBranding';
import { displayCategory } from '@/config/libraryCategories';

function Meta({ k, v, tid }) {
  return <div className="flex justify-between gap-4 py-1 border-b border-dashed last:border-0 text-sm"><span className="text-muted-foreground">{k}</span><span className="text-right break-all" data-testid={tid}>{v ?? '—'}</span></div>;
}

export default function FormLibraryItemPage() {
  const { libraryId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const brand = useLibraryBranding();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const { data } = await api.get(`/forms/library/${libraryId}`); setItem(data.data); }
    catch (e) { setError(libraryError(e)); setItem(null); }
    setLoading(false);
  }, [libraryId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <LibraryLoading label="Loading library master…" />;
  if (error) return <div className="space-y-4"><Button variant="ghost" onClick={() => navigate('/forms/library')} data-testid="library-back-btn"><ArrowLeft className="w-4 h-4 mr-2" />Template Library</Button><LibraryErrorState error={error} onRetry={load} /></div>;

  const missing = item.missing_capabilities || [];
  const blocked = !item.effective ? 'This is a retired library version (history only). Only the current version can be imported.'
    : missing.length ? `Requires capabilities not available for your organisation: ${missing.join(', ')}.` : null;
  const mayImport = canImportLibrary(hasPermission);

  return (
    <div className="space-y-6" data-testid="form-library-item-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" aria-label="Back to Template Library" onClick={() => navigate('/forms/library')} data-testid="library-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight" data-testid="library-item-name">{item.name}</h1>
              <Badge variant={item.effective ? 'default' : 'outline'} data-testid="library-item-status">{item.effective ? 'Current' : 'Retired'}</Badge>
              {item.classification && <Badge variant="outline">{item.classification}</Badge>}
            </div>
            <p className="text-muted-foreground text-sm">{item.library_key} · v{item.pack_version} · {item.module_type}</p>
          </div>
        </div>
        {mayImport && (
          <Button onClick={() => setImportOpen(true)} disabled={!!blocked} title={blocked || undefined} data-testid="library-import-btn"><Download className="w-4 h-4 mr-2" />Import as draft</Button>
        )}
      </div>

      {blocked && (
        <div role="status" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="library-item-blocked">
          <History className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{blocked}{!item.effective && item.active_library_id && <> <Link className="underline" to={`/forms/library/${item.active_library_id}`} data-testid="library-item-current-link">Open current version (v{item.active_pack_version})</Link></>}</span>
        </div>
      )}

      {item.description && <Card><CardContent className="py-3"><p className="text-sm text-muted-foreground" data-testid="library-item-description">{item.description}</p></CardContent></Card>}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Eye className="w-4 h-4" />Fields ({(item.schema_json || []).length}) — read-only master</CardTitle></CardHeader>
          <CardContent><LibrarySchemaPreview fields={item.schema_json || []} rules={item.rules_json || []} engineVersion={item.rules_engine_version ?? null} /></CardContent>
        </Card>
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Library className="w-4 h-4" />Master details</CardTitle></CardHeader>
            <CardContent>
              <Meta k="Pack" v={brand.packLabel(item.pack_version)} tid="library-item-pack" />
              <Meta k="Publisher" v={brand.publisher} tid="library-item-publisher" />
              <Meta k="Category" v={<>{displayCategory(item.library_key)}{item.category && <span className="block text-xs text-muted-foreground font-mono">pack category: {item.category}</span>}</>} tid="library-item-category" />
              <Meta k="Approval by default" v={item.approval_required_default ? 'Yes' : 'No'} tid="library-item-approval" />
              <Meta k="Signature mode" v={item.signature_mode === 'TYPED' ? 'Typed-name acknowledgement' : item.signature_mode} tid="library-item-signature" />
              <Meta k="Checksum" v={<span className="font-mono text-xs">{item.checksum}</span>} tid="library-item-checksum" />
              {(item.source_refs || []).length > 0 && (
                <div className="pt-2 text-sm"><span className="text-muted-foreground">Evidence references</span>
                  <ul className="list-disc pl-5 text-xs mt-1" data-testid="library-item-source-refs">{item.source_refs.map((s, i) => <li key={i}>{sourceRefLabel(s)} <span className="font-mono text-muted-foreground">({s.ref})</span></li>)}</ul>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Your organisation's copies</CardTitle></CardHeader>
            <CardContent>
              {(item.org_copies || []).length === 0 ? <p className="text-sm text-muted-foreground" data-testid="library-item-no-copies">Not imported yet.</p> : (
                <ul className="space-y-1 text-sm" data-testid="library-item-copies">
                  {item.org_copies.map(c => (
                    <li key={c.template_id} className="flex items-center justify-between gap-2">
                      <Link className="underline" to={`/forms/templates/${c.template_id}`} data-testid={`library-copy-${c.template_id}`}>{c.form_code}</Link>
                      <span className="text-xs text-muted-foreground">{c.status} · from v{c.source_pack_version}{c.update_available && <Badge variant="secondary" className="ml-2 text-[10px]">Update available</Badge>}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {mayImport && (
        <LibraryImportDialog item={item} open={importOpen} onOpenChange={setImportOpen}
          onImported={(t) => { toast.success(`Imported as ${t.form_code} (draft)`); navigate(`/forms/templates/${t.id}`); }} />
      )}
    </div>
  );
}
